// @flow

import React from 'react'
import { render } from 'react-dom'
import { Provider } from 'react-redux'
import { ConnectedRouter } from 'react-router-redux'
import { fetchClientList } from './modules/client'
import { fetchInitialData, hasLoadedAppData, hasReceivedAppContext } from './modules/loading'
import {
  exchangeCode,
  fetchAuthProfile,
  login,
  logout,
  setIsDoneAuthorizing,
  verifyExternalUserAccess } from './modules/authProfile'
import { fetchAppContext, prePopulatePartnerIds, setAppContext, setShowContext } from './modules/appContext'
import { fetchCSRFToken } from './modules/csrf'
import { nordAdmins, partnerAccessAdmin } from './constants/memberships';
import { hasMembership, hasRole, isEmpty, parseQuery } from './utils/helpers'
import { push } from 'react-router-redux'
import store, { history } from './store'
import Index from './pages/index'

const target = document.querySelector('#root')

render(
  <Provider store={store}>
    <ConnectedRouter history={history}>
      <div>
        <Index />
      </div>
    </ConnectedRouter>
  </Provider>,
  target
)

/**
 *  Where all the intial loading of data happens. This is also listening
 *  for /callback from NAuth Service. When this happens, it needs to exchange
 *  code for token in cookie before loading data
 */
switch (true) {
  case /callback/.test(history.location.pathname):
    let params = parseQuery(history.location.search.substring(1))

    if (isEmpty(params)) {
  // no params, clear any possible existing tokens and force login.
      store.dispatch(fetchCSRFToken())
        .then(() => store.dispatch(logout()))
        .then(() => store.dispatch(login({ shouldForceReauthentication: true })))
    } else if (params.error === 'access_denied' || !isValidState(params.state)) {
      store.dispatch(push('/unauthorized'))
    } else {
      const route = determineSubpath()
      store.dispatch(fetchCSRFToken())
        .then(() => store.dispatch(exchangeCode(params)))
        .then(() => configureUser(route))
    }
    break
  case /login/.test(history.location.pathname):
    // SkipIWA is true, force user credentials
    // configureUser(true)
    store.dispatch(login({ shouldForceReauthentication: true }))
    break
  case /loggedOut/.test(history.location.pathname):
    break
  case /application/.test(history.location.pathname):
    if (history.location.pathname.includes('-expired')) {
      store.dispatch(hasReceivedAppContext(true))  // display static expired-page
    } else {
      let appClientId = history.location.pathname.split('/')[2]
      store.dispatch(fetchCSRFToken())
        .then(() => store.dispatch(setAppContext(appClientId)))
    }
    break
  default:
    sessionStorage.setItem('pathBeforeLoginRedirect', window.location.pathname + window.location.search)
    store.dispatch(login({ shouldForceReauthentication: false }))
}

function configureUser(route) {
  return store.dispatch(hasLoadedAppData(false))
    .then(() => store.dispatch(fetchCSRFToken()))
    .then(() => store.dispatch(fetchAuthProfile()))
    .then(() => store.dispatch(fetchAppContext()))  // load any cached app-context into store
    .then(() => fetchPartnersContext())  // load any cached partners-context into store
    .then(() => store.dispatch(fetchClientList()))
    .then(() => initializeApp(route))
    .then(() => store.dispatch(hasLoadedAppData(true)))
    .catch(err => {console.log(err); store.dispatch(push('/unauthorized'))})
}

function fetchPartnersContext() {
  let partners = JSON.parse(window.localStorage.getItem('partners'))
  if (partners && partners.length > 0) {
    return store.dispatch(prePopulatePartnerIds(partners))
  }
  return () => new Promise((resolve) => {})
}

async function initializeApp(route) {
  let authProfile = store.getState().authProfile
  let app = store.getState().appContext.appInfo.id
  let partners = store.getState().appContext.initialValues.partnerIds
  // cached case
  if (app && partners && partners.length > 0) {
    if (!hasMembership(authProfile.userMemberships, nordAdmins) && !hasRole(authProfile.userRoles, partnerAccessAdmin)) {
      // User is a PA user, need to make sure they only go to /profile
      route = '/profile'
    }
    return store.dispatch(fetchInitialData())
    .then(() => store.dispatch(setIsDoneAuthorizing(true)))
    .then(() => store.dispatch(push(route ? route : '/')))
    .catch((err) => store.dispatch(push('/unauthorized')))
  }

  // Nord Admins
  if (hasMembership(authProfile.userMemberships, nordAdmins)) {
    return store.dispatch(setShowContext(true))
      .then(() => store.dispatch(push('/appContextDialog')))
  }
  // External Users
  let appIds = authProfile.applicationIds
  if (!appIds) {
    // User doesn't have applicationIds in userinfo, need to fetch true profile.
    // This is a one-time occurance (authProfile will have appIds array next time they log in)
    try {
      let user = await store.dispatch(verifyExternalUserAccess(authProfile.userId))
      appIds = user.payload.applicationIds
    } catch(e) {
      // call failed or there is more wrong with their profile -> unauthorized
    return store.dispatch(setIsDoneAuthorizing(true))
        .then(() => store.dispatch(push('/unauthorized')))
    }
  }
  // PA Admins
  if (hasRole(authProfile.userRoles, partnerAccessAdmin)) {
    if (appIds.length > 1) {
    // If PA Admin is admin for multiple apps, they will need to select one
      return store.dispatch(setShowContext(true))
        .then(() => store.dispatch(setIsDoneAuthorizing(true)))
        .then(() => store.dispatch(push('/appContextDialog')))
    }

    if (appIds.length === 1) {
    // Otherwise, default to their only app and fetch data
      return store.dispatch(setAppContext(appIds[0]))
        .then(() => store.dispatch(setIsDoneAuthorizing(true)))
        .then(() => store.dispatch(fetchInitialData()))
        .then(() => store.dispatch(push(route ? route : '/')))
        .catch((err) => store.dispatch(push('/unauthorized')))
    }
    // User has no apps
    return store.dispatch(setIsDoneAuthorizing(true))
      .then(() => store.dispatch(push('/unauthorized')))
  }

  // Partner Users
  if (appIds.length > 0) {
    // TODO: consider how PA Users should get app context set (now that the side nav app url is visible to them)
    if (app) {
      return store.dispatch(setIsDoneAuthorizing(true))
        .then(() => store.dispatch(fetchInitialData()))
        .then(() => store.dispatch(push('/profile')))
    }
    // For PA Users, doesn't matter which app is their context (if they have more than 1)
    // all they can do is set password / question
    return store.dispatch(setAppContext(appIds[0]))
      .then(() => store.dispatch(setIsDoneAuthorizing(true)))
      .then(() => store.dispatch(fetchInitialData()))
      .then(() => store.dispatch(push('/profile')))
  }
  // User has no apps
  return store.dispatch(setIsDoneAuthorizing(true))
    .then(() => store.dispatch(push('/unauthorized')))
}

function determineSubpath() {
  const path = sessionStorage.getItem('pathBeforeLoginRedirect')
  if (path) {
    sessionStorage.removeItem('pathBeforeLoginRedirect')
    return path
  } else {
    return '/'
  }
}

function isValidState(callbackState) {
  return callbackState === sessionStorage.getItem('localState')
}
